import { Component, computed, inject, input, output, signal } from '@angular/core';
import { FormGroup, ReactiveFormsModule } from '@angular/forms';
import { firstValueFrom } from 'rxjs';
import { PrimengImports } from '../../../../../shared/primeng/primeng.imports';
import {
  PropertyIntakeUploads,
  optionLabel,
} from '../../models/property-intake.model';
import { LeadService } from '../../../../../core/services/lead.service';
import { FeasibilityQuestionOption } from '../../../../../core/models/lead.model';

@Component({
  selector: 'krc-review-step',
  standalone: true,
  imports: [ReactiveFormsModule, PrimengImports],
  templateUrl: './review-step.component.html',
  styleUrl: './review-step.component.scss',
})
export class ReviewStepComponent {
  private readonly leadService = inject(LeadService);

  readonly contactProperty = input.required<Record<string, any>>();
  readonly projectPurpose = input.required<Record<string, any>>();
  readonly intendedUse = input.required<Record<string, any>>();
  readonly preferredContact = input<string | null>(null);
  readonly uploads = input.required<PropertyIntakeUploads>();
  readonly consentForm = input.required<FormGroup>();

  readonly editStep = output<number>();
  readonly submitForm = output<void>();

  private readonly buildTimelineOptions = signal<FeasibilityQuestionOption[]>([]);
  private readonly intendedUseOptions = signal<FeasibilityQuestionOption[]>([]);
  private readonly primaryInterestOptions = signal<FeasibilityQuestionOption[]>([]);
  private readonly aboutAduOptions = signal<FeasibilityQuestionOption[]>([]);

  constructor() {
    firstValueFrom(this.leadService.getFeasibilityQuestions('INTAKE', 'CONTACT'))
      .then(questions => {
        const buildTimeline = questions.find(q => q.questionCode === 'INTAKE_BUILD_TIMELINE');
        if (buildTimeline?.options?.length) this.buildTimelineOptions.set(buildTimeline.options);
      })
      .catch(() => { /* keep the built-in fallback above */ });

    firstValueFrom(this.leadService.getFeasibilityQuestions('INTAKE', 'INTENDED_USE'))
      .then(questions => {
        const byCode = new Map(questions.map(q => [q.questionCode, q]));
        this.intendedUseOptions.set(byCode.get('INTAKE_INTENDED_USE')?.options ?? []);
        this.primaryInterestOptions.set(byCode.get('INTAKE_PRIMARY_INTEREST')?.options ?? []);
        this.aboutAduOptions.set(byCode.get('INTAKE_ABOUT_ADU')?.options ?? []);
      })
      .catch(() => { /* labels fall back to '—' via optionLabel */ });
  }

  readonly ownerStatusLabel = computed(() => (this.contactProperty()['isOwner'] === 'yes' ? 'Property Owner' : 'Not the Owner'));
  readonly buildTimelineLabel = computed(() => optionLabel(this.buildTimelineOptions(), this.contactProperty()['buildTimeline']));

  readonly intendedUseLabels = computed(() => {
    const selected: string[] = this.intendedUse()['intendedUse'] ?? [];
    return selected.map(value => optionLabel(this.intendedUseOptions(), value)).join(', ') || '—';
  });
  readonly intendedUseOtherDescription = computed(() => this.intendedUse()['intendedUseOtherDescription'] ?? '');
  readonly primaryInterestLabels = computed(() => {
    const selected: string[] = this.intendedUse()['primaryInterest'] ?? [];
    return selected.map(value => optionLabel(this.primaryInterestOptions(), value)).join(', ') || '—';
  });
  readonly aboutAduLabel = computed(() => optionLabel(this.aboutAduOptions(), this.intendedUse()['aboutAdu']));

  readonly uploadedFiles = computed(() => {
    const uploads = this.uploads();
    return [
      ...Object.values(uploads.photos).filter((file): file is File => !!file),
      ...Object.values(uploads.documents).filter((file): file is File => !!file),
    ];
  });

  fileIcon(file: File): string {
    return file.type.startsWith('image/') || file.type.startsWith('video/') ? 'pi pi-image' : 'pi pi-file';
  }
}
